// Dashboard utilities for managing pinned entities and dashboard configuration

import { useEffect, useState } from 'react';

export type DashboardCardType = 'entity';

export interface DashboardEntityCard {
  type: 'entity';
  entityId: string;
  workspaceId: string;
  id: string; // unique card id
  order?: number;
}

export type DockItem =
  | {
      type: 'entity';
      name: string;
      icon?: string;
      entityId: string;
      workspaceId: string;
    }
  | {
      type: 'url';
      name: string;
      url: string;
      icon?: string;
      workspaceId: string;
    };

export type DashboardCard = DashboardEntityCard;

export interface DashboardConfig {
  cards?: DashboardCard[];
  dockItems?: DockItem[];
  version: number;
}

const DASHBOARD_STORAGE_KEY = 'finsight_dashboard_config';
const DASHBOARD_VERSION = 1;

// Event dispatching for dashboard changes
const dispatchDashboardChange = () => {
  window.dispatchEvent(new CustomEvent('dashboardChanged'));
};

function getDefaultDashboardConfig(): DashboardConfig {
  return {
    cards: [],
    version: DASHBOARD_VERSION,
  };
}

export function getDashboardConfig(): DashboardConfig {
  try {
    const stored = localStorage.getItem(DASHBOARD_STORAGE_KEY);
    if (!stored) {
      return getDefaultDashboardConfig();
    }

    const config = JSON.parse(stored) as DashboardConfig;

    // Handle version migration if needed
    if (config.version !== DASHBOARD_VERSION) {
      return getDefaultDashboardConfig();
    }

    return config;
  } catch (error) {
    console.error('Failed to load dashboard config:', error);
    return getDefaultDashboardConfig();
  }
}

export function saveDashboardConfig(config: DashboardConfig): void {
  try {
    localStorage.setItem(DASHBOARD_STORAGE_KEY, JSON.stringify(config));
  } catch (error) {
    console.error('Failed to save dashboard config:', error);
  }
}

export function addEntityToBoard(
  entityId: string,
  workspaceId: string | null,
): void {
  if (!workspaceId) return;
  const config = getDashboardConfig();

  // Check if entity is already pinned
  const existingCard = config.cards?.find(
    (card) =>
      card.type === 'entity' &&
      card.entityId === entityId &&
      card.workspaceId === workspaceId,
  );

  if (existingCard) {
    return; // Already pinned
  }

  const newCard: DashboardEntityCard = {
    type: 'entity',
    entityId,
    workspaceId,
    id: `entity_${entityId}_${Date.now()}`,
    order: config.cards?.length || 0,
  };

  config.cards = config.cards ? [...config.cards, newCard] : [newCard];
  saveDashboardConfig(config);
  dispatchDashboardChange();
}

export function removeEntityFromBoard(
  entityId: string,
  workspaceId: string | null,
): void {
  const config = getDashboardConfig();

  config.cards = config.cards?.filter(
    (card) =>
      !(
        card.type === 'entity' &&
        card.entityId === entityId &&
        card.workspaceId === workspaceId
      ),
  );

  // Reorder remaining cards
  config.cards?.forEach((card, index) => {
    card.order = index;
  });

  saveDashboardConfig(config);
  dispatchDashboardChange();
}

export function isEntityPinned(
  entityId: string,
  workspaceId: string | null,
): boolean {
  const config = getDashboardConfig();
  return (
    config.cards?.some(
      (card) =>
        card.type === 'entity' &&
        card.entityId === entityId &&
        card.workspaceId === workspaceId,
    ) ?? false
  );
}

export function getPinnedEntityIds(workspaceId: string | null): string[] {
  const config = getDashboardConfig();
  return (
    config.cards
      ?.filter(
        (card) => card.type === 'entity' && card.workspaceId === workspaceId,
      )
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((card) => (card as DashboardEntityCard).entityId) ?? []
  );
}

export function reorderDashboardCards(cardIds: string[]): void {
  const config = getDashboardConfig();

  const cardMap = new Map(config.cards?.map((card) => [card.id, card]));

  config.cards = cardIds
    .map((id) => cardMap.get(id))
    .filter((card): card is DashboardCard => card !== undefined)
    .map((card, index) => ({ ...card, order: index }));

  saveDashboardConfig(config);
  dispatchDashboardChange();
}

export function addDockItem(item: DockItem): void {
  const config = getDashboardConfig();

  // Check if item is already in dock
  const exists = config.dockItems?.some((dockItem) => {
    if (
      dockItem.type !== item.type ||
      dockItem.workspaceId !== item.workspaceId
    ) {
      return false;
    }
    if (item.type === 'entity' && dockItem.type === 'entity') {
      return dockItem.entityId === item.entityId;
    }
    if (item.type === 'url' && dockItem.type === 'url') {
      return dockItem.url === item.url;
    }
    return false;
  });

  if (exists) {
    return; // Already in dock
  }

  config.dockItems = config.dockItems ? [...config.dockItems, item] : [item];

  saveDashboardConfig(config);
  dispatchDashboardChange();
}

export function removeDockItem(item: DockItem): void {
  const config = getDashboardConfig();

  config.dockItems = config.dockItems?.filter((dockItem) => {
    if (
      dockItem.type !== item.type ||
      dockItem.workspaceId !== item.workspaceId
    ) {
      return true;
    }
    if (item.type === 'entity' && dockItem.type === 'entity') {
      return dockItem.entityId !== item.entityId;
    }
    if (item.type === 'url' && dockItem.type === 'url') {
      return dockItem.url !== item.url;
    }
    return true;
  });

  saveDashboardConfig(config);
  dispatchDashboardChange();
}

export function updateDockItem(
  oldItem: DockItem,
  updates: Partial<Pick<DockItem, 'name' | 'icon'>> & { url?: string },
): void {
  const config = getDashboardConfig();

  config.dockItems = config.dockItems?.map((dockItem) => {
    if (
      dockItem.type !== oldItem.type ||
      dockItem.workspaceId !== oldItem.workspaceId
    ) {
      return dockItem;
    }
    if (oldItem.type === 'entity' && dockItem.type === 'entity') {
      return dockItem.entityId === oldItem.entityId
        ? { ...dockItem, ...updates }
        : dockItem;
    }
    if (oldItem.type === 'url' && dockItem.type === 'url') {
      return dockItem.url === oldItem.url
        ? { ...dockItem, ...updates }
        : dockItem;
    }
    return dockItem;
  });

  saveDashboardConfig(config);
  dispatchDashboardChange();
}

export function getDockItems(workspaceId: string | null): DockItem[] {
  const config = getDashboardConfig();
  return (
    config.dockItems?.filter((item) => item.workspaceId === workspaceId) || []
  );
}

export function isEntityInDock(
  entityId: string,
  workspaceId: string | null,
): boolean {
  const config = getDashboardConfig();
  return (
    config.dockItems?.some(
      (item) =>
        item.type === 'entity' &&
        item.entityId === entityId &&
        item.workspaceId === workspaceId,
    ) ?? false
  );
}

export function moveDockItemLeft(item: DockItem): void {
  const config = getDashboardConfig();
  if (!config.dockItems) return;

  const workspaceItems = config.dockItems.filter(
    (i) => i.workspaceId === item.workspaceId,
  );
  const currentIndex = workspaceItems.findIndex((i) => {
    if (i.type !== item.type || i.workspaceId !== item.workspaceId)
      return false;
    if (item.type === 'entity' && i.type === 'entity') {
      return i.entityId === item.entityId;
    }
    if (item.type === 'url' && i.type === 'url') {
      return i.url === item.url;
    }
    return false;
  });

  if (currentIndex <= 0) return; // Already at the start or not found

  // Swap with previous item
  const allItems = [...config.dockItems];
  const globalCurrentIndex = allItems.findIndex((i) => {
    if (i.type !== item.type || i.workspaceId !== item.workspaceId)
      return false;
    if (item.type === 'entity' && i.type === 'entity') {
      return i.entityId === item.entityId;
    }
    if (item.type === 'url' && i.type === 'url') {
      return i.url === item.url;
    }
    return false;
  });
  const prevItem = workspaceItems[currentIndex - 1];
  const globalPrevIndex = allItems.findIndex((i) => {
    if (i.type !== prevItem.type || i.workspaceId !== prevItem.workspaceId)
      return false;
    if (prevItem.type === 'entity' && i.type === 'entity') {
      return i.entityId === prevItem.entityId;
    }
    if (prevItem.type === 'url' && i.type === 'url') {
      return i.url === prevItem.url;
    }
    return false;
  });

  if (globalCurrentIndex !== -1 && globalPrevIndex !== -1) {
    [allItems[globalCurrentIndex], allItems[globalPrevIndex]] = [
      allItems[globalPrevIndex],
      allItems[globalCurrentIndex],
    ];
    config.dockItems = allItems;
    saveDashboardConfig(config);
    dispatchDashboardChange();
  }
}

export function moveDockItemRight(item: DockItem): void {
  const config = getDashboardConfig();
  if (!config.dockItems) return;

  const workspaceItems = config.dockItems.filter(
    (i) => i.workspaceId === item.workspaceId,
  );
  const currentIndex = workspaceItems.findIndex((i) => {
    if (i.type !== item.type || i.workspaceId !== item.workspaceId)
      return false;
    if (item.type === 'entity' && i.type === 'entity') {
      return i.entityId === item.entityId;
    }
    if (item.type === 'url' && i.type === 'url') {
      return i.url === item.url;
    }
    return false;
  });

  if (currentIndex === -1 || currentIndex >= workspaceItems.length - 1) {
    return; // At the end or not found
  }

  // Swap with next item
  const allItems = [...config.dockItems];
  const globalCurrentIndex = allItems.findIndex((i) => {
    if (i.type !== item.type || i.workspaceId !== item.workspaceId)
      return false;
    if (item.type === 'entity' && i.type === 'entity') {
      return i.entityId === item.entityId;
    }
    if (item.type === 'url' && i.type === 'url') {
      return i.url === item.url;
    }
    return false;
  });
  const nextItem = workspaceItems[currentIndex + 1];
  const globalNextIndex = allItems.findIndex((i) => {
    if (i.type !== nextItem.type || i.workspaceId !== nextItem.workspaceId)
      return false;
    if (nextItem.type === 'entity' && i.type === 'entity') {
      return i.entityId === nextItem.entityId;
    }
    if (nextItem.type === 'url' && i.type === 'url') {
      return i.url === nextItem.url;
    }
    return false;
  });

  if (globalCurrentIndex !== -1 && globalNextIndex !== -1) {
    [allItems[globalCurrentIndex], allItems[globalNextIndex]] = [
      allItems[globalNextIndex],
      allItems[globalCurrentIndex],
    ];
    config.dockItems = allItems;
    saveDashboardConfig(config);
    dispatchDashboardChange();
  }
}

export function useDockChangeEvent() {
  const [, setDummyState] = useState(0);

  useEffect(() => {
    const handleChange = () => {
      setDummyState((prev) => prev + 1);
    };

    window.addEventListener('dashboardChanged', handleChange);
    return () => {
      window.removeEventListener('dashboardChanged', handleChange);
    };
  }, []);

  return null;
}
